import { useRank } from '../../hooks/useRank';
import GlassyIcon from '../common/GlassyIcon';
import { FireIcon, BookOpenIcon, CheckBadgeIcon, SpeakerWaveIcon } from '@heroicons/react/24/solid';
import { formatListeningTime } from '../../utils/listeningStats';

const STATS = [
  { key: 'streak',    label: 'Streak',   icon: FireIcon,        colorClass: 'red',    accentColor: 'var(--color-stat-streak)'   },
  { key: 'learned',   label: 'Learned',  icon: BookOpenIcon,    colorClass: 'emerald',accentColor: 'var(--color-stat-learned)'  },
  { key: 'reviewed',  label: 'Reviewed', icon: CheckBadgeIcon,  colorClass: 'amber',  accentColor: 'var(--color-stat-reviewed)' },
  { key: 'listening', label: 'Listened', icon: SpeakerWaveIcon, colorClass: 'violet', accentColor: 'var(--color-stat-accuracy)' },
];

// Bubbles bleed heavily off top/bottom edges so they appear as crescents only,
// staying above/below the vertically-centered icon+text content.
// ↓ Adjust this array to reposition bubbles
const BUBBLES = [
  { size: 58, color: 'var(--color-stat-streak)',   style: { top:    '-40px', left:  '1%'  } },
  { size: 50, color: 'var(--color-stat-streak)',   style: { bottom: '-32px', left:  '16%' }, desktopOnly: true },
  { size: 58, color: 'var(--color-stat-learned)',  style: { bottom: '-36px', left:  '30%' } },
  { size: 38, color: 'var(--color-stat-learned)',  style: { top:    '-26px', left:  '46%' }, desktopOnly: true },
  { size: 54, color: 'var(--color-stat-reviewed)', style: { top:    '-34px', left:  '52%' } },
  { size: 40, color: 'var(--color-stat-reviewed)', style: { bottom: '-26px', left:  '74%' }, desktopOnly: true },
  { size: 62, color: 'var(--color-stat-accuracy)', style: { bottom: '-38px', right: '2%'  } },
  { size: 38, color: 'var(--color-stat-accuracy)', style: { top:    '-24px', right: '14%' }, desktopOnly: true },
];

// ↓ Control bubble visibility here — lower = more subtle (try 0.10–0.25)
const BUBBLE_OPACITY = 0.1;

const RankCard = ({ streak, totalLearned, totalReviewed, listeningSeconds }) => {
  const rank = useRank();
  if (!rank) return null;

  const cv = rank.tier.cssVar;

  const values = {
    streak:    streak || 0,
    learned:   totalLearned || 0,
    reviewed:  totalReviewed || 0,
    // Hours-only on mobile (compact) so "19h 10m" doesn't wrap; full value on desktop.
    listening: (
      <>
        <span className="sm:hidden">{formatListeningTime(listeningSeconds, { compact: true })}</span>
        <span className="hidden sm:inline">{formatListeningTime(listeningSeconds)}</span>
      </>
    ),
  };

  return (
    <div className="relative rounded-xl overflow-hidden backdrop-blur-sm border-2 border-border bg-background-secondary/70 animate-fade-in transition-transform duration-200">
      <div className="flex flex-col sm:flex-row items-stretch">

        {/* ── Rank section ── */}
        <div className="relative overflow-hidden flex-shrink-0 sm:basis-1/4">
          <div className="absolute inset-0 opacity-[0.02]"
            style={{ background: rank.tierBg }} />
          <div className="absolute inset-0 pointer-events-none" style={{ opacity: BUBBLE_OPACITY }}>
            <div className="absolute rounded-full top-0 right-0 translate-x-5 -translate-y-5"
              style={{ width: 72, height: 72, background: rank.tierBg }} />
            <div className="absolute rounded-full bottom-0 left-0 -translate-x-4 translate-y-4"
              style={{ width: 48, height: 48, background: rank.tierBg }} />
          </div>

          <div className="relative z-10 flex sm:flex-col items-center sm:justify-center p-3 sm:p-4 gap-3 sm:gap-2">
            <div className="text-center flex-shrink-0">
              <div
                className="text-2xl sm:text-3xl lg:text-4xl font-black leading-none"
                style={rank.isMaxLevel
                  ? { background: rank.tierBg, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }
                  : { color: `var(${cv})` }}
              >
                {rank.label}
              </div>
              <div className="hidden lg:block text-[10px] font-semibold text-content-secondary tracking-widest uppercase mt-1">
                {rank.tier.reading}
              </div>
            </div>
            <div className="flex-1 sm:flex-none sm:w-full space-y-1">
              <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: 'var(--color-border)' }}>
                <div className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${rank.progress * 100}%`, background: rank.tierBg }} />
              </div>
              <div className="text-[10px] lg:text-[11px] text-content-secondary leading-none sm:text-center whitespace-nowrap">
                {rank.isMaxLevel
                  ? 'Max Level'
                  : `${(rank.score - rank.currentThreshold).toLocaleString()} / ${(rank.nextThreshold - rank.currentThreshold).toLocaleString()} pts`}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile: horizontal divider / Desktop: vertical divider */}
        <div className="sm:hidden h-px bg-border" />
        <div className="hidden sm:block w-px self-stretch bg-border flex-shrink-0" />

        {/* ── Stats ── */}
        <div className="relative flex-1 overflow-visible">
          {/* Bubbles — adjust BUBBLES array and BUBBLE_OPACITY above to tune */}
          <div className="absolute inset-0 pointer-events-none" style={{ opacity: BUBBLE_OPACITY }}>
            {BUBBLES.map((b, i) => (
              <div key={i}
                className={`absolute rounded-full${b.desktopOnly ? ' hidden sm:block' : ''}`}
                style={{ width: b.size, height: b.size, background: b.color, ...b.style }} />
            ))}
          </div>

          {/* Always 4 columns — icons hidden on mobile to keep height compact */}
          <div className="relative z-10 grid grid-cols-4">
            {STATS.map((stat) => (
              <div key={stat.key}
                className="flex flex-col items-center justify-center p-2 sm:p-3 lg:p-4 text-center gap-0.5 sm:gap-1"
              >
                <div className="hidden sm:block">
                  <GlassyIcon icon={stat.icon} size="sm" color={stat.colorClass} />
                </div>
                <p className="text-base sm:text-xl lg:text-2xl font-bold leading-tight" style={{ color: stat.accentColor }}>
                  {values[stat.key]}
                </p>
                <p className="text-[8px] sm:text-[9px] lg:text-[10px] font-semibold text-content-secondary uppercase tracking-wider opacity-80">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default RankCard;
